import type { JSX } from 'react';
import { useEffect, useRef } from 'react';
import type { LogLine } from '@teams-avatar/shared';
import { Button } from './ui';

const LEVEL_STYLES: Record<LogLine['level'], string> = {
  debug: 'text-fg-muted',
  info: 'text-fg-subtle',
  warn: 'text-[color:#8a5a00]',
  error: 'text-danger',
};

/**
 * Live-Log einer Session. Ohne das sucht man Fehler blind — deshalb ist es
 * direkt an der Session und nicht in einem separaten Bereich.
 */
export function LogPanel({
  lines,
  onClose,
}: {
  lines: LogLine[];
  onClose: () => void;
}): JSX.Element {
  const boxRef = useRef<HTMLDivElement>(null);
  /** Mitlaufen, solange der Leser unten steht. Scrollt er hoch, nicht stoeren. */
  const folgen = useRef(true);

  /*
   * Nur den Log-Kasten scrollen, NICHT die Seite.
   *
   * Vorher stand hier scrollIntoView auf einem Element im Kasten. Das scrollt
   * jeden scrollbaren Vorfahren mit, also auch das Dokument: bei jeder neuen
   * Logzeile sprang die ganze Seite nach unten und das weisse Log-Feld fuellte
   * kurz den Blick. Von aussen sah das aus wie ein Flackern oder ein Neuladen.
   */
  useEffect(() => {
    const box = boxRef.current;
    if (box === null || !folgen.current) return;
    box.scrollTop = box.scrollHeight;
  }, [lines.length]);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-bg">
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <span className="text-[11px] tracking-wide text-fg-muted uppercase">
          Live-Log ({lines.length})
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          schliessen
        </Button>
      </div>
      <div
        ref={boxRef}
        onScroll={(e) => {
          const box = e.currentTarget;
          // Kleine Toleranz: sonst gilt man schon bei einem Pixel Abstand als
          // "hochgescrollt" und der Log bleibt stehen.
          folgen.current = box.scrollHeight - box.scrollTop - box.clientHeight < 40;
        }}
        className="max-h-72 overflow-y-auto px-4 py-3 font-mono text-xs leading-relaxed"
      >
        {lines.length === 0 ? (
          <p className="text-fg-muted">Noch keine Ausgaben. Neue Zeilen erscheinen live.</p>
        ) : (
          lines.map((line, index) => (
            <div key={index} className={LEVEL_STYLES[line.level]}>
              <span className="tabular text-fg-muted">{line.ts.slice(11, 19)}</span>{' '}
              <span className="text-fg-muted">[{line.source}]</span> {line.msg}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
